import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, useMediaQuery, useTheme } from "@mui/material";
import { TbArrowLeft } from "react-icons/tb";
import { ModalCloseButton } from "../../../Components/Molecules/ValutDialog";
import { TransferStepper } from "../../../Components/Molecules";

const MODAL_PAPER_MOBILE = "!flex !flex-col !h-full !overflow-x-hidden !shadow-card app-card";
const MODAL_PAPER_BASE =
  "!rounded-2xl !shadow-card app-card !m-4 !flex !flex-col !max-h-[90vh] !w-full !max-w-[calc(100vw-2rem)] !overflow-x-hidden";
const MODAL_PAPER_CLASS = `${MODAL_PAPER_BASE} sm:!max-w-3xl`;
const MODAL_PAPER_RECEIPT_CLASS = `${MODAL_PAPER_BASE} sm:!max-w-4xl`;

const STEP_SUBTITLES = [
  "Informe o CPF ou CNPJ do destinatário.",
  "Informe o valor e a descrição da transferência.",
  "Revise os dados antes de confirmar.",
  "Informe sua senha transacional para concluir.",
  "Transferência finalizada.",
];

export const InternalTransferModal = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const contentRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isSelfDestination, setIsSelfDestination] = useState(false);
  const [transactionSucceeded, setTransactionSucceeded] = useState(null);
  const isReceiptStep = currentStep >= 4;

  useEffect(() => {
    if (!open) {
      setCurrentStep(0);
      setIsConfirming(false);
      setIsSearching(false);
      setIsSelfDestination(false);
      setTransactionSucceeded(null);
    }
  }, [open]);

  const handleStepChange = useCallback(({
    step,
    transactionStatus,
    isConfirming: confirming,
    isSearching: searching,
    isSelfDestination: selfDest,
  }) => {
    setCurrentStep(step);
    setIsConfirming(confirming);
    setIsSearching(Boolean(searching));
    setIsSelfDestination(Boolean(selfDest));
    if (step >= 4) {
      setTransactionSucceeded(transactionStatus);
    } else {
      setTransactionSucceeded(null);
    }
  }, []);

  const handleDialogClose = (_, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    onClose();
  };

  const handleNewTransfer = () => {
    contentRef.current?.reset();
    setCurrentStep(0);
    setTransactionSucceeded(null);
    setIsConfirming(false);
    setIsSelfDestination(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown
      fullScreen={isMobile}
      fullWidth
      maxWidth={isReceiptStep ? "lg" : "md"}
      scroll="paper"
      PaperProps={{
        className: isMobile
          ? MODAL_PAPER_MOBILE
          : isReceiptStep
            ? MODAL_PAPER_RECEIPT_CLASS
            : MODAL_PAPER_CLASS,
      }}
    >
      <div className="relative flex min-h-0 flex-1 flex-col">
        <ModalCloseButton
          onClick={onClose}
          disabled={isConfirming || isSearching}
          className="absolute right-3 top-3 z-10"
        />

        <div
          className={`min-h-0 flex-1 overflow-y-auto px-4 pb-2 pt-4 sm:px-5 ${
            !isMobile && !isReceiptStep ? "max-h-[70vh]" : ""
          }`}
        >
          <h2 className="pr-8 text-lg font-bold text-brand-dark">
            {isReceiptStep ? "Comprovante de transferência" : "Transferir entre contas"}
          </h2>
          <p className="mt-0.5 text-sm text-gray-500">
            {isReceiptStep
              ? transactionSucceeded
                ? "Entre contas Valut."
                : "Não foi possível concluir a transferência."
              : STEP_SUBTITLES[currentStep]}
          </p>

          <div className="mt-3">
            <TransferStepper
              ref={contentRef}
              inModal
              footerInModal
              isActive={open}
              initialType="internal"
              onClose={onClose}
              onStepChange={handleStepChange}
            />
          </div>
        </div>

        <div className="mt-auto shrink-0 border-t border-gray-100 px-4 pb-4 pt-3 sm:px-5">
          {currentStep === 0 && (
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                className="btn-primary w-full sm:w-auto"
                onClick={() => contentRef.current?.submitSearch()}
                disabled={isSearching}
              >
                {isSearching ? "Buscando conta..." : "Buscar conta"}
              </button>
            </div>
          )}

          {currentStep === 1 && (
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              <button
                type="button"
                className="btn-back w-full sm:w-auto"
                onClick={() => contentRef.current?.goBack()}
              >
                <TbArrowLeft className="h-4 w-4" />
                Voltar
              </button>
              <button
                type="button"
                className="btn-primary w-full sm:w-auto"
                onClick={() => contentRef.current?.submitTransferInfo()}
                disabled={isSelfDestination}
              >
                Continuar
              </button>
            </div>
          )}

          {currentStep === 2 && (
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              <button
                type="button"
                className="btn-back w-full sm:w-auto"
                onClick={() => contentRef.current?.goBack()}
              >
                <TbArrowLeft className="h-4 w-4" />
                Voltar
              </button>
              <button
                type="button"
                className="btn-primary w-full sm:w-auto"
                onClick={() => contentRef.current?.submit()}
                disabled={isSelfDestination}
              >
                Confirmar transferência
              </button>
            </div>
          )}

          {currentStep === 3 && (
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              <button
                type="button"
                className="btn-back w-full sm:w-auto"
                onClick={() => contentRef.current?.goBack()}
                disabled={isConfirming}
              >
                <TbArrowLeft className="h-4 w-4" />
                Voltar
              </button>
              <button
                type="button"
                className="btn-primary w-full sm:w-auto"
                onClick={() => contentRef.current?.confirmPassword()}
                disabled={isConfirming}
              >
                {isConfirming ? "Validando senha..." : "Confirmar senha"}
              </button>
            </div>
          )}

          {currentStep >= 4 && (
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              <button type="button" className="btn-outline w-full sm:w-auto" onClick={onClose}>
                Fechar
              </button>
              <button
                type="button"
                className="btn-primary w-full sm:w-auto"
                onClick={handleNewTransfer}
              >
                Nova transferência
              </button>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};
